/**
 * Locale-aware date/time formatting for RFC 3339 wire timestamps.
 *
 * The API sends RFC 3339 UTC timestamps ("2026-08-10T12:00:00Z"); the UI shows
 * them in the viewer's local time zone unless `opts.timeZone` overrides it
 * (pass `timeZone: "UTC"` in tests for deterministic output).
 *
 * Parsing is strict: `parseRfc3339` validates with a regex plus a calendar
 * round-trip before constructing a Date, so `Date.parse` leniency (bare dates,
 * missing offsets, out-of-range fields, trailing garbage) is rejected.
 */

const RFC3339_RE =
  /^(\d{4})-(\d{2})-(\d{2})[Tt](\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,9}))?(?:[Zz]|([+-])(\d{2}):(\d{2}))$/;

/**
 * Parse a strict RFC 3339 timestamp into a Date.
 *
 * Accepts `YYYY-MM-DDTHH:MM:SS[.fff]Z` or a `±HH:MM` numeric offset ("T"/"Z"
 * may be lowercase per the RFC). Fractional seconds beyond milliseconds are
 * truncated. Throws RangeError on anything else — including calendar-invalid
 * dates (2026-02-30), out-of-range fields, missing offsets, and leap seconds
 * (`:60`, which a JS Date cannot represent).
 */
export function parseRfc3339(value: string): Date {
  const m = RFC3339_RE.exec(value);
  if (!m) {
    throw new RangeError(`Invalid RFC 3339 timestamp: "${value}"`);
  }
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const hour = Number(m[4]);
  const minute = Number(m[5]);
  const second = Number(m[6]);
  const fraction = m[7];
  const offsetSign = m[8];
  const offsetHour = Number(m[9] ?? "0");
  const offsetMinute = Number(m[10] ?? "0");

  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    hour > 23 ||
    minute > 59 ||
    second > 59 ||
    offsetHour > 23 ||
    offsetMinute > 59
  ) {
    throw new RangeError(`Out-of-range field in RFC 3339 timestamp: "${value}"`);
  }

  // setUTCFullYear (not Date.UTC) so years 0000–0099 are not remapped to 19xx.
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    throw new RangeError(`Invalid calendar date in RFC 3339 timestamp: "${value}"`);
  }

  const ms = fraction ? Number(fraction.padEnd(3, "0").slice(0, 3)) : 0;
  date.setUTCHours(hour, minute, second, ms);

  const offsetMinutes = offsetSign
    ? (offsetHour * 60 + offsetMinute) * (offsetSign === "-" ? -1 : 1)
    : 0;
  return offsetMinutes === 0 ? date : new Date(date.getTime() - offsetMinutes * 60_000);
}

/** Serialize a Date as RFC 3339 UTC with a "Z" designator and millisecond precision. */
export function toRfc3339(date: Date): string {
  if (Number.isNaN(date.getTime())) {
    throw new RangeError("Cannot serialize an invalid Date as RFC 3339");
  }
  return date.toISOString();
}

const DATE_COMPONENT_KEYS = [
  "weekday",
  "era",
  "year",
  "month",
  "day",
] as const satisfies readonly (keyof Intl.DateTimeFormatOptions)[];

const TIME_COMPONENT_KEYS = [
  "hour",
  "minute",
  "second",
  "fractionalSecondDigits",
  "dayPeriod",
  "timeZoneName",
] as const satisfies readonly (keyof Intl.DateTimeFormatOptions)[];

/**
 * Merge caller opts over our style defaults. Intl throws when dateStyle /
 * timeStyle are mixed with component options (year, hour, …), so when the
 * caller passes any component option we drop the style defaults entirely and
 * use their options as given.
 */
function resolveOptions(
  defaults: Intl.DateTimeFormatOptions,
  componentKeys: readonly (keyof Intl.DateTimeFormatOptions)[],
  opts: Intl.DateTimeFormatOptions | undefined,
): Intl.DateTimeFormatOptions {
  if (!opts) return defaults;
  const hasComponentOptions = componentKeys.some((key) => opts[key] !== undefined);
  return hasComponentOptions ? opts : { ...defaults, ...opts };
}

const CALENDAR_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Format a calendar date — `2026-01-31`, a period end or a due date — which is
 * a day on a calendar and not an instant in time.
 *
 * It is formatted in UTC deliberately: parsed as midnight UTC and rendered in
 * the reader's zone, a due date of the 31st shows as the 30th to everyone west
 * of Greenwich. The day the server said is the day the reader sees.
 */
export function formatCalendarDate(
  isoDate: string,
  locale: string,
  opts?: Intl.DateTimeFormatOptions,
): string {
  const parts = CALENDAR_DATE_RE.exec(isoDate);
  if (!parts) throw new RangeError(`Invalid calendar date: "${isoDate}"`);
  const date = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) throw new RangeError(`Invalid calendar date: "${isoDate}"`);
  return new Intl.DateTimeFormat(
    locale,
    resolveOptions({ dateStyle: "medium", timeZone: "UTC" }, DATE_COMPONENT_KEYS, opts),
  ).format(date);
}

/** Format the date portion of an RFC 3339 timestamp (default `dateStyle: "medium"`). */
export function formatDate(
  isoRfc3339: string,
  locale: string,
  opts?: Intl.DateTimeFormatOptions,
): string {
  const date = parseRfc3339(isoRfc3339);
  return new Intl.DateTimeFormat(
    locale,
    resolveOptions({ dateStyle: "medium" }, DATE_COMPONENT_KEYS, opts),
  ).format(date);
}

/**
 * Format date + time of an RFC 3339 timestamp
 * (default `dateStyle: "medium", timeStyle: "short"`).
 */
export function formatDateTime(
  isoRfc3339: string,
  locale: string,
  opts?: Intl.DateTimeFormatOptions,
): string {
  const date = parseRfc3339(isoRfc3339);
  return new Intl.DateTimeFormat(
    locale,
    resolveOptions(
      { dateStyle: "medium", timeStyle: "short" },
      [...DATE_COMPONENT_KEYS, ...TIME_COMPONENT_KEYS],
      opts,
    ),
  ).format(date);
}

/** Format the time portion of an RFC 3339 timestamp (default `timeStyle: "short"`). */
export function formatTime(
  isoRfc3339: string,
  locale: string,
  opts?: Intl.DateTimeFormatOptions,
): string {
  const date = parseRfc3339(isoRfc3339);
  return new Intl.DateTimeFormat(
    locale,
    resolveOptions({ timeStyle: "short" }, TIME_COMPONENT_KEYS, opts),
  ).format(date);
}

const RELATIVE_DIVISIONS: readonly { amount: number; unit: Intl.RelativeTimeFormatUnit }[] = [
  { amount: 60, unit: "second" },
  { amount: 60, unit: "minute" },
  { amount: 24, unit: "hour" },
  { amount: 7, unit: "day" },
  { amount: 4.34524, unit: "week" },
  { amount: 12, unit: "month" },
  { amount: Number.POSITIVE_INFINITY, unit: "year" },
];

/**
 * Format an RFC 3339 timestamp relative to `now` (default: current time) with
 * automatic granularity from seconds up to years, e.g. "30 seconds ago",
 * "in 2 hours", "yesterday" (`numeric: "auto"` phrasing where the locale has it).
 */
export function formatRelative(isoRfc3339: string, locale: string, now: Date = new Date()): string {
  const target = parseRfc3339(isoRfc3339);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  let duration = (target.getTime() - now.getTime()) / 1000;
  for (const division of RELATIVE_DIVISIONS) {
    if (Math.abs(duration) < division.amount) {
      return rtf.format(Math.round(duration), division.unit);
    }
    duration /= division.amount;
  }
  // Unreachable: the last division's amount is Infinity.
  return rtf.format(Math.round(duration), "year");
}
